/**
 * PDP Variant Configurator
 *
 * Custom web component that renders metafield-driven variant selectors
 * (pills, swatches) with progressive filtering. Reads configuration from
 * a pdp_config metaobject serialized as JSON by pdp-variant-data.liquid.
 *
 * Contains its own Add to Cart form, quantity selector, and Find a Dealer
 * link. Integrates with the Horizon product form by dispatching compatible
 * variant events.
 */

class PdpVariantConfigurator extends HTMLElement {
  constructor() {
    super();
    this.selectedOptions = new Map();
    this.resolvedVariant = null;
    this.data = null;
    this.abortController = null;
    this._viewModelsMode = false;
    this._soldOutMode = false;
    this.effectiveOptionCount = 0;
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.init();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this._removeViewModelsHandler();
    this._removeSoldOutHandler();
  }

  _viewModelsHandler = (e) => {
    e.preventDefault();
    const modelsTab = document.querySelector('[data-tab="models-specs"]');
    if (modelsTab) {
      modelsTab.click();
      modelsTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  _addViewModelsHandler() {
    if (!this._viewModelsMode && this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', this._viewModelsHandler);
      this._viewModelsMode = true;
    }
  }

  _removeViewModelsHandler() {
    if (this._viewModelsMode && this.addToCartBtn) {
      this.addToCartBtn.removeEventListener('click', this._viewModelsHandler);
      this._viewModelsMode = false;
    }
  }

  _soldOutHandler = (e) => {
    e.preventDefault();
    return;
  };

  _addSoldOutHandler() {
    if (!this._soldOutMode && this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', this._soldOutHandler);
      this._soldOutMode = true;
    }
  }

  _removeSoldOutHandler() {
    if (this._soldOutMode && this.addToCartBtn) {
      this.addToCartBtn.removeEventListener('click', this._soldOutHandler);
      this._soldOutMode = false;
    }
  }

  init() {
    const jsonEl = this.querySelector('script[data-pdp-variant-data]');
    if (!jsonEl) return;

    try {
      this.data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('[PDP Configurator] Failed to parse variant data:', e);
      return;
    }

    if (!this.data.config?.variantOptions?.length) return;

    this.dataset.productId = String(this.data.product.id);

    this.optionsContainer = this.querySelector('[data-options-container]');
    this.selectionDisplay = this.querySelector('[data-selection-display]');
    this.selectionSku = this.querySelector('[data-selection-sku]');
    this.selectionPrice = this.querySelector('[data-selection-price]');
    this.selectionComparePrice = this.querySelector('[data-selection-compare-price]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtyMinus = this.querySelector('[data-qty-minus]');
    this.qtyPlus = this.querySelector('[data-qty-plus]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.cartForm = this.querySelector('[data-configurator-form]');
    this.variantStatus = this.querySelector('[data-variant-status]');

    // If the product has varying prices, show a "From $X.XX" label
    // in the product-price block and cache it for restoring on deselect
    this._initialPriceNode = null;
    const initSection = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (initSection && this.data.variants.length > 1) {
      const prices = this.data.variants.map(v => v.price).filter(p => p != null);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      if (minPrice !== maxPrice) {
        const priceEl = initSection.querySelector('product-price');
        const priceContainer = priceEl?.querySelector('[ref="priceContainer"]');
        if (priceContainer) {
          const fromTemplate = this.dataset.tPriceFrom || 'From __PRICE__';
          const prefixText = fromTemplate.split('__PRICE__')[0].trim();

          const prefixSpan = document.createElement('span');
          prefixSpan.className = 'product-price__from-label';
          prefixSpan.textContent = prefixText;

          const priceSpan = document.createElement('span');
          priceSpan.className = 'price';
          priceSpan.textContent = this.formatMoney(minPrice);

          priceContainer.replaceChildren(prefixSpan, priceSpan);
          this._initialPriceNode = priceContainer.cloneNode(true);
        }
      }
    }

    // Wire up -/+ quantity buttons
    if (this.qtyMinus && this.qtyInput && this.qtyPlus && window.PdpUtils) {
      window.PdpUtils.setupQtyStepper(this.qtyMinus, this.qtyInput, this.qtyPlus);
    }

    // Handle cart form submission via AJAX
    if (this.cartForm) {
      this.cartForm.addEventListener('submit', (e) => this.handleCartSubmit(e), { signal: this.abortController.signal });
    }

    // Clear skeleton/noscript fallback
    if (this.optionsContainer) {
      this.optionsContainer.innerHTML = '';
    }

    this.render();
    this.setAttribute('data-initialized', '');
  }

  /**
   * Render all option groups based on the config
   */
  render() {
    const { variantOptions } = this.data.config;
    const container = this.optionsContainer;
    if (!container) return;

    container.innerHTML = '';
    this.effectiveOptionCount = 0;

    variantOptions.forEach((option) => {
      const values = this.getUniqueValues(option);
      if (values.length === 0) {
        // No variants have this metafield — skip entirely, don't count it
        return;
      }
      this.effectiveOptionCount++;
      if (values.length === 1) {
        // Auto-select the single value so variant resolution still works
        this.selectedOptions.set(option.metafield, values[0].value);
        // Show as static label/value text instead of a pill button
        const spec = document.createElement('div');
        spec.className = 'pdp-configurator__spec';
        spec.innerHTML = `<span class="pdp-configurator__spec-label">${option.label}</span> <span class="pdp-configurator__spec-value">${values[0].label}</span>`;
        container.appendChild(spec);
        return;
      }
      const group = this.createOptionGroup(option, values);
      container.appendChild(group);
    });

    this.updateAvailability();
    this.autoSelectSingleOption();
    this.updateSelection();
    this.updateBuyButton();
  }

  /**
   * If only one option group is rendered and it has exactly one enabled
   * value, auto-select it so the variant resolves immediately on load.
   */
  autoSelectSingleOption() {
    const groups = this.optionsContainer.querySelectorAll('.pdp-configurator__group');
    if (groups.length !== 1) return;

    const group = groups[0];
    const metafield = group.dataset.metafield;
    if (this.selectedOptions.has(metafield)) return;

    const enabledButtons = group.querySelectorAll('[data-value]:not(:disabled)');
    if (enabledButtons.length !== 1) return;

    this.selectedOptions.set(metafield, enabledButtons[0].dataset.value);
    this.updateAvailability();
  }

  /**
   * Create a single option group (fieldset with pills or swatches)
   */
  createOptionGroup(option, values) {
    const group = document.createElement('fieldset');
    group.className = 'pdp-configurator__group';
    group.dataset.metafield = option.metafield;

    const legend = document.createElement('legend');
    legend.className = 'pdp-configurator__group-label';

    const labelText = document.createElement('span');
    labelText.textContent = option.label;
    legend.appendChild(labelText);

    if (option.tooltip) {
      group.setAttribute('aria-label', option.label);

      const tooltipWrap = document.createElement('span');
      tooltipWrap.className = 'pdp-configurator__tooltip';

      const tooltipIcon = document.createElement('button');
      tooltipIcon.type = 'button';
      tooltipIcon.className = 'pdp-configurator__tooltip-icon';
      tooltipIcon.setAttribute('aria-label', option.tooltip);
      tooltipIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 12" width="12" height="12"><path fill="currentColor" d="M6.88889 8.88889c0 .1758-.05213.34766-.1498.49384-.09768.14617-.2365.2601-.39893.32738-.16242.06728-.34114.08488-.51357.05059-.17243-.0343-.33081-.11896-.45513-.24327-.12431-.12432-.20897-.2827-.24327-.45513s-.01669-.35115.05059-.51358c.06727-.16242.1812-.30124.32738-.39892C5.65234 8.05213 5.8242 8 6 8c.23575 0 .46184.09365.62854.26035s.26035.39279.26035.62854M12 6c0 1.18669-.3519 2.34672-1.0112 3.33342-.6593.98668-1.59634 1.75568-2.6927 2.20988-1.09636.4541-2.30275.5729-3.46664.3414s-2.23298-.8029-3.0721-1.6421C.918247 9.40352.346802 8.33443.115291 7.17054S.00259972 4.80025.456725 3.7039C.910851 2.60754 1.67989 1.67047 2.66658 1.01118 3.65328.351894 4.81331 0 6 0c1.59076.00176457 3.11586.634472 4.2407 1.75931C11.3655 2.88414 11.9982 4.40924 12 6m-1.3333 0c0-.92298-.2737-1.82523-.78651-2.59266-.51278-.76743-1.24161-1.36557-2.09433-1.71878S5.99482 1.24294 5.08958 1.423c-.90524.18007-1.73676.62452-2.38941 1.27717-.65264.65264-1.0971 1.48416-1.27717 2.38941-.18006.90524-.08764 1.84355.26556 2.69627.35321.85273.95135 1.58156 1.71878 2.09434.76743.51281 1.66968.78651 2.59266.78651 1.23727-.0014 2.42349-.4934 3.29837-1.36833C10.1733 8.42348 10.6653 7.23727 10.6667 6M6 2.44444c-1.34778 0-2.44444.99667-2.44444 2.22223v.22222c0 .17681.07024.34638.19526.4714.12502.12503.29459.19526.4714.19526s.34638-.07023.47141-.19526c.12502-.12502.19526-.29459.19526-.4714v-.22222c0-.49.5-.88889 1.11111-.88889s1.11111.39889 1.11111.88889-.5.88888-1.11111.88888c-.17681 0-.34638.07024-.4714.19527-.12503.12502-.19527.29459-.19527.4714v.44445c-.00039.1647.0602.32373.17009.44641.1099.12269.26132.20035.42507.21801.16376.01767.32826-.02591.46179-.12233.13354-.09643.22665-.23887.26139-.39987 1.03222-.26 1.79278-1.12167 1.79278-2.14222 0-1.22556-1.09667-2.22223-2.44445-2.22223" opacity="0.3"/></svg>`;

      const tooltipPopup = document.createElement('span');
      tooltipPopup.className = 'pdp-tooltip pdp-configurator__tooltip-popup';
      tooltipPopup.setAttribute('aria-hidden', 'true');
      tooltipPopup.textContent = option.tooltip;

      tooltipWrap.appendChild(tooltipIcon);
      tooltipWrap.appendChild(tooltipPopup);

      // Desktop: show/hide on hover
      tooltipWrap.addEventListener('mouseenter', () => tooltipWrap.classList.add('is-active'), { signal: this.abortController.signal });
      tooltipWrap.addEventListener('mouseleave', () => tooltipWrap.classList.remove('is-active'), { signal: this.abortController.signal });

      // Mobile: toggle on tap; close when tapping outside
      tooltipIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        tooltipWrap.classList.toggle('is-active');
      }, { signal: this.abortController.signal });

      document.addEventListener('click', () => tooltipWrap.classList.remove('is-active'), { signal: this.abortController.signal });

      // Keyboard: show on focus, hide on blur
      tooltipIcon.addEventListener('focus', () => tooltipWrap.classList.add('is-active'), { signal: this.abortController.signal });
      tooltipIcon.addEventListener('blur', () => tooltipWrap.classList.remove('is-active'), { signal: this.abortController.signal });

      legend.appendChild(tooltipWrap);
    }

    // For color swatches, add a selected-value indicator after the tooltip
    if (option.display === 'color_swatches') {
      const selectedValue = document.createElement('span');
      selectedValue.className = 'pdp-configurator__selected-value';
      selectedValue.dataset.metafield = option.metafield;
      legend.appendChild(selectedValue);
    }

    group.appendChild(legend);

    const optionsWrap = document.createElement('div');
    optionsWrap.className = `pdp-configurator__options-wrap pdp-configurator__options-wrap--${option.display || 'pills'}`;

    values.forEach((val) => {
      const btn = this.createOptionButton(option, val);
      optionsWrap.appendChild(btn);
    });

    group.appendChild(optionsWrap);
    return group;
  }

  /**
   * Get unique values for an option across all variants
   */
  getUniqueValues(option) {
    const seen = new Map();
    const { metafield, secondary_metafield: secondaryMetafield } = option;

    this.data.variants.forEach((variant) => {
      const val = variant.metafields[metafield];
      if (val == null || val === '' || /^[\s\-\u2013\u2014]+$/.test(String(val))) return;

      const key = String(val);
      if (!seen.has(key)) {
        const entry = { value: val, label: key };
        if (secondaryMetafield) {
          const secondary = variant.metafields[secondaryMetafield];
          entry.label = secondary ? `${val} / ${secondary}` : key;
          entry.secondaryValue = secondary;
        }
        // For swatches, grab the first variant image
        if (option.display === 'color_swatches' && variant.featuredImage) {
          entry.image = variant.featuredImage;
        }
        seen.set(key, entry);
      } else if (option.display === 'color_swatches' && !seen.get(key).image && variant.featuredImage) {
        seen.get(key).image = variant.featuredImage;
      }
    });

    return Array.from(seen.values());
  }

  /**
   * Create a single option button (pill or swatch)
   */
  createOptionButton(option, valueEntry) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.metafield = option.metafield;
    btn.dataset.value = valueEntry.value;

    if (option.display === 'color_swatches' && valueEntry.image) {
      btn.className = 'pdp-configurator__swatch';
      const img = document.createElement('img');
      img.src = valueEntry.image.replace(/\?.*/, '') + '?width=160&height=160&crop=center';
      img.alt = valueEntry.label;
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;
      btn.appendChild(img);
    } else {
      btn.className = 'pdp-configurator__pill';
      btn.textContent = valueEntry.label;
    }

    btn.setAttribute('aria-label', `${option.label}: ${valueEntry.label}`);
    btn.addEventListener('click', () => this.handleOptionClick(option.metafield, valueEntry.value), { signal: this.abortController.signal });

    return btn;
  }

  /**
   * Handle an option button click
   */
  handleOptionClick(metafield, value) {
    // Toggle: clicking the same value deselects it
    if (this.selectedOptions.get(metafield) === value) {
      this.selectedOptions.delete(metafield);
    } else {
      this.selectedOptions.set(metafield, value);
    }

    this.updateAvailability();
    this.updateSelectedValueLabels();
    this.updateSelection();
    this.updateBuyButton();
    this.updateGalleryForColorSelection();
  }

  /**
   * Update the selected-value labels shown next to color swatch group legends
   */
  updateSelectedValueLabels() {
    const indicators = this.optionsContainer.querySelectorAll('.pdp-configurator__selected-value');
    indicators.forEach((el) => {
      const metafield = el.dataset.metafield;
      const selectedVal = this.selectedOptions.get(metafield);
      el.textContent = selectedVal != null ? selectedVal : '';
    });
  }

  /**
   * When a color swatch option is selected but the variant isn't fully
   * resolved yet, find the first matching variant and show its image
   * in the gallery so the user gets immediate visual feedback.
   */
  updateGalleryForColorSelection() {
    // Skip if a variant is already fully resolved — updateBuyButton handles that
    if (this.resolvedVariant) return;

    // Find color_swatches options that have a selection
    const colorOption = this.data.config.variantOptions.find(
      (opt) => opt.display === 'color_swatches' && this.selectedOptions.has(opt.metafield)
    );
    if (!colorOption) return;

    const colorValue = this.selectedOptions.get(colorOption.metafield);
    const match = this.data.variants.find(
      (v) => String(v.metafields[colorOption.metafield]) === String(colorValue) && v.featuredImage
    );
    if (match) {
      this.updateMediaGallery(match);
    }
  }

  /**
   * Filter variants to those matching all currently selected options
   */
  getFilteredVariants() {
    return this.data.variants.filter((variant) => {
      for (const [metafield, value] of this.selectedOptions) {
        const variantVal = variant.metafields[metafield];
        if (String(variantVal) !== String(value)) return false;
      }
      return true;
    });
  }

  /**
   * Update which option buttons are available/selected/disabled
   */
  updateAvailability() {
    const groups = this.optionsContainer.querySelectorAll('.pdp-configurator__group');

    groups.forEach((group) => {
      const metafield = group.dataset.metafield;
      const buttons = group.querySelectorAll('[data-value]');

      // Get variants matching all selections EXCEPT this group's metafield
      const otherSelections = new Map(this.selectedOptions);
      otherSelections.delete(metafield);

      const compatibleVariants = this.data.variants.filter((variant) => {
        for (const [key, val] of otherSelections) {
          if (String(variant.metafields[key]) !== String(val)) return false;
        }
        return true;
      });

      const availableValues = new Set(
        compatibleVariants
          .filter((v) => v.available)
          .map((v) => String(v.metafields[metafield]))
          .filter((v) => v !== 'null' && v !== 'undefined' && v !== '')
      );

      buttons.forEach((btn) => {
        const val = btn.dataset.value;
        const selectedVal = this.selectedOptions.get(metafield);
        const isSelected = selectedVal != null && String(selectedVal) === val;
        const isAvailable = availableValues.has(val);
        const isSwatch = btn.classList.contains('pdp-configurator__swatch');
        const prefix = isSwatch ? 'pdp-configurator__swatch' : 'pdp-configurator__pill';

        btn.classList.toggle(`${prefix}--selected`, isSelected);
        btn.classList.toggle(`${prefix}--unavailable`, !isAvailable);
        btn.setAttribute('aria-pressed', isSelected);
        btn.disabled = !isAvailable;
      });
    });
  }

  /**
   * Update the SELECTION display area based on resolved variant
   */
  updateSelection() {
    const selectedCount = this.selectedOptions.size;

    if (selectedCount === 0) {
      this.resolvedVariant = null;
      this.hideSelection();
      return;
    }

    const filtered = this.getFilteredVariants();

    if (selectedCount >= this.effectiveOptionCount && filtered.length === 1) {
      // Single variant resolved
      this.resolvedVariant = filtered[0];
      this.showSelection(filtered[0]);
    } else {
      this.resolvedVariant = null;
      this.hideSelection();
    }
  }

  showSelection(variant) {
    if (!this.selectionDisplay) return;

    this.selectionDisplay.hidden = false;
    this.selectionSku.textContent = variant.sku || variant.title;
    this.selectionPrice.textContent = this.formatMoney(variant.price);

    if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
      this.selectionComparePrice.textContent = this.formatMoney(variant.compareAtPrice);
      this.selectionComparePrice.hidden = false;
    } else {
      this.selectionComparePrice.textContent = '';
      this.selectionComparePrice.hidden = true;
    }
  }

  hideSelection() {
    if (this.selectionDisplay) {
      this.selectionDisplay.hidden = true;
    }
  }

  /**
   * Update the buy button state and the hidden variant ID input
   */
  updateBuyButton() {
    const selectedCount = this.selectedOptions.size;
    const filtered = this.getFilteredVariants();
    const allSelected = selectedCount >= this.effectiveOptionCount;

    if (allSelected && filtered.length === 1) {
      const variant = filtered[0];
      if (this.variantIdInput) this.variantIdInput.value = variant.id;

      if (this.addToCartBtn) {
        this._removeViewModelsHandler();
        this._removeSoldOutHandler();
        if (variant.available) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = this.dataset.tAddToCart || 'Add to Cart';
          this.setQtyDisabled(false);
          this.announceStatus(variant.sku
            ? `${variant.sku} selected, ${this.formatMoney(variant.price)}`
            : `Variant selected, ${this.formatMoney(variant.price)}`);

          // Also update the external buy-buttons variant ID so sticky bar stays in sync
          this.syncExternalVariantId(variant.id);

          // Notify sticky bar of resolved variant
          document.dispatchEvent(new CustomEvent('configurator:variant-resolved', {
            detail: { variant, sku: variant.sku, price: variant.price, compareAtPrice: variant.compareAtPrice, available: variant.available }
          }));
        } else {
          // Unavailable — show sold out
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.textContent = this.dataset.tSoldOut || 'Sold Out';
          this._addSoldOutHandler();
          this.setQtyDisabled(true);
          this.announceStatus('Selected variant is sold out');
          document.dispatchEvent(new CustomEvent('configurator:variant-resolved', { detail: { variant: null } }));
        }
      }

      // Update the external price component if present
      this.updatePriceDisplay(variant);

      // Update URL with variant parameter
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update the media gallery via the Section Rendering API
      this.updateMediaGallery(variant);
    } else if (allSelected) {
      // Multiple matches or no exact match — show "View Models"
      if (this.variantIdInput) this.variantIdInput.value = '';
      if (this.addToCartBtn) {
        this._removeSoldOutHandler();
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = this.dataset.tViewModels || 'View All Models';
        this._addViewModelsHandler();
      }
      this.announceStatus('');
      this.restorePriceDisplay();
      document.dispatchEvent(new CustomEvent('configurator:variant-resolved', { detail: { variant: null } }));
    } else {
      // Not fully selected — disable
      if (this.variantIdInput) this.variantIdInput.value = '';
      if (this.addToCartBtn) {
        this._removeViewModelsHandler();
        this._removeSoldOutHandler();
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = this.dataset.tSelectOptions || 'Select Options';
      }
      this.announceStatus('');
      this.restorePriceDisplay();
      document.dispatchEvent(new CustomEvent('configurator:variant-resolved', { detail: { variant: null } }));
    }
  }

  /**
   * Keep the external buy-buttons hidden variant ID in sync for the sticky bar
   */
  syncExternalVariantId(variantId) {
    const section = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (!section) return;
    const externalInput = section.querySelector('.buy-buttons-block input[name="id"]');
    if (externalInput) externalInput.value = variantId;
  }

  /**
   * Handle AJAX cart form submission
   */
  async handleCartSubmit(e) {
    e.preventDefault();

    const variantId = this.variantIdInput?.value;
    if (!variantId) return;

    const quantity = parseInt(this.qtyInput?.value, 10) || 1;
    if (!window.PdpUtils) return;
    await window.PdpUtils.addToCart(parseInt(variantId, 10), quantity, this.addToCartBtn, this);
  }


  /**
   * Update the product-price component with new variant pricing.
   * Provides immediate visual feedback; the full HTML-based update
   * follows when updateMediaGallery's fetch completes.
   */
  updatePriceDisplay(variant) {
    const section = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (!section) return;

    const priceEl = section.querySelector('product-price');
    if (!priceEl) return;

    const fromLabel = priceEl.querySelector('.product-price__from-label');
    if (fromLabel) fromLabel.remove();

    const priceSpan = priceEl.querySelector('.price');
    const comparePriceSpan = priceEl.querySelector('.compare-at-price');

    if (priceSpan) priceSpan.textContent = this.formatMoney(variant.price);
    if (comparePriceSpan) {
      comparePriceSpan.textContent =
        variant.compareAtPrice && variant.compareAtPrice > variant.price
          ? this.formatMoney(variant.compareAtPrice)
          : '';
    }
  }

  /**
   * Restore the initial "From" price display when no variant is resolved
   */
  restorePriceDisplay() {
    if (!this._initialPriceNode) return;
    const section = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (!section) return;
    const priceEl = section.querySelector('product-price');
    if (!priceEl) return;
    const priceContainer = priceEl.querySelector('[ref="priceContainer"]');
    if (priceContainer) priceContainer.replaceWith(this._initialPriceNode.cloneNode(true));
  }

  /**
   * Fetch the section HTML for the resolved variant and dispatch variant:update
   * so the media gallery swaps itself via Horizon's built-in handler.
   */
  updateMediaGallery(variant) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const sectionId = section.dataset.sectionId || section.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    // Abort any in-flight gallery fetch
    this._galleryAbort?.abort();
    this._galleryAbort = new AbortController();

    const productUrl = this.data.product.url;
    const url = `${productUrl}?variant=${variant.id}&section_id=${sectionId}`;

    fetch(url, { signal: this._galleryAbort.signal })
      .then((res) => res.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');

        // Dispatch from the configurator so the event bubbles up to the
        // section where product-price and media-gallery are listening.
        this.dispatchEvent(new CustomEvent('variant:update', {
          bubbles: true,
          detail: {
            resource: { id: variant.id, available: variant.available },
            sourceId: this.id || '',
            data: {
              html,
              productId: String(this.data.product.id),
            },
          },
        }));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error('[PDP Configurator] Gallery fetch failed:', err);
      });
  }

  setQtyDisabled(disabled) {
    if (this.qtyInput) this.qtyInput.disabled = disabled;
    if (this.qtyMinus) this.qtyMinus.disabled = disabled;
    if (this.qtyPlus) this.qtyPlus.disabled = disabled;
  }

  announceStatus(message) {
    if (!this.variantStatus) return;
    clearTimeout(this._statusTimer);
    this.variantStatus.textContent = '';
    if (!message) return;
    requestAnimationFrame(() => {
      this.variantStatus.textContent = message;
      this._statusTimer = setTimeout(() => {
        this.variantStatus.textContent = '';
      }, 1000);
    });
  }

  formatMoney(cents) {
    return window.PdpUtils?.formatMoney(cents) || '';
  }
}

if (!customElements.get('pdp-variant-configurator')) {
  customElements.define('pdp-variant-configurator', PdpVariantConfigurator);
}
